import { useState, useEffect, useMemo } from 'react';
import { Link, router } from '@inertiajs/react';
import TeacherShell, { TeacherPageHeader } from '../../../components/layout/TeacherShell';
import { Card, DynamicIsland } from '../../../components/ui';
import SessionMapGrid from '../../../components/teacher/SessionMapGrid';
import SessionMapLegend from '../../../components/teacher/SessionMapLegend';
import SubmissionExpand from '../../../components/teacher/SubmissionExpand';
import CompactUnitView from '../../../components/teacher/CompactUnitView';
import { cn } from '../../../lib/cn';

export default function Submissions({ activity, questions, students, grid_data, lesson_context, unit_context, grammar_concepts = [] }) {
  const [gridData, setGridData] = useState(grid_data);
  const [expanded, setExpanded] = useState(null); // { studentId, questionId }
  const [filter, setFilter] = useState('all');

  // Sync gridData when server props change (e.g. after Inertia reload)
  useEffect(() => { setGridData(grid_data); }, [grid_data]);

  // Refresh data when tab regains focus (picks up question edits made in other tabs)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        router.reload({ preserveScroll: true });
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Count needs-review responses across all grid data
  const needsReviewCount = useMemo(() => {
    let count = 0;
    gridData.forEach(row => {
      Object.values(row.responses).forEach(cell => {
        if (cell.needs_review) count++;
      });
    });
    return count;
  }, [gridData]);

  // Count responses that are verified but need evaluation
  const shortAnswerQIds = useMemo(() => new Set(
    questions.filter(q => ['short_answer', 'essay', 'fill_blank'].includes(q.question_type)).map(q => q.id)
  ), [questions]);

  const unevaluatedCount = useMemo(() => {
    let count = 0;
    gridData.forEach(row => {
      Object.entries(row.responses).forEach(([qId, cell]) => {
        const eligible = cell.transcription_verified || cell.content_type === 'typed';
        if (shortAnswerQIds.has(Number(qId)) &&
            eligible && cell.content && cell.content !== '[blank]' &&
            (!cell.grammar_evaluated || cell.grammar_score == null)) {
          count++;
        }
      });
    });
    return count;
  }, [gridData, shortAnswerQIds]);

  const [evaluating, setEvaluating] = useState(false);
  const [revokingStudentId, setRevokingStudentId] = useState(null);

  const handleRevokeScanToken = async (student) => {
    if (!confirm(`Revoke ${student.full_name}'s printed worksheet? Its QR code will stop working.`)) return;
    setRevokingStudentId(student.id);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const res = await fetch(`/teacher/activities/${activity.id}/revoke_scan_token?student_id=${student.id}`, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': csrfToken, 'Accept': 'application/json' },
      });
      if (res.ok) {
        router.reload({ preserveScroll: true });
      } else {
        alert(`Couldn't revoke ${student.full_name}'s worksheet. Please try again.`);
      }
    } catch (e) {
      console.error('Revoke scan token failed:', e);
      alert(`Couldn't revoke ${student.full_name}'s worksheet. Please try again.`);
    } finally {
      setRevokingStudentId(null);
    }
  };

  const handleEvaluateAll = async () => {
    setEvaluating(true);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const res = await fetch(`/teacher/activities/${activity.id}/evaluate_all`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken, 'Accept': 'application/json' },
      });
      if (res.ok) {
        // Reload the page data to pick up all new scores
        router.reload({ preserveScroll: true });
      }
    } catch (e) {
      console.error('Evaluate all failed:', e);
    } finally {
      setEvaluating(false);
    }
  };

  // Filter students based on active filter
  const filteredStudents = useMemo(() => {
    if (filter === 'all') return students;
    return students.filter(s => {
      const row = gridData.find(r => r.student_id === s.id);
      if (!row) return false;
      return Object.values(row.responses).some(cell => cell.needs_review);
    });
  }, [students, gridData, filter]);

  const handleCellClick = ({ studentId, questionId }) => {
    if (expanded?.studentId === studentId && expanded?.questionId === questionId) {
      setExpanded(null);
    } else {
      setExpanded({ studentId, questionId });
    }
  };

  const handleSaved = (studentId, questionId, updatedResponse) => {
    const question = questions.find(q => q.id === questionId);
    const isMCQ = question && (question.question_type === 'mcq' || question.question_type === 'multiple_choice');

    setGridData(prev => prev.map(row => {
      if (row.student_id !== studentId) return row;
      const existingCell = row.responses[questionId] || {};

      // Derive display_value from the updated data
      let display_value;
      if (isMCQ) {
        display_value = updatedResponse.selected_index != null
          ? String.fromCharCode(65 + updatedResponse.selected_index)
          : null;
      } else {
        if (updatedResponse.score != null) {
          display_value = `${Math.round(updatedResponse.score)}`;
        } else {
          const text = updatedResponse.content || '';
          display_value = text && text !== '[blank]'
            ? `${text.split(/\s+/).length}w`
            : null;
        }
      }

      // Derive status
      const hasAnswer = isMCQ
        ? updatedResponse.selected_index != null
        : updatedResponse.content && updatedResponse.content !== '[blank]';

      return {
        ...row,
        responses: {
          ...row.responses,
          [questionId]: {
            ...existingCell,
            content: updatedResponse.content,
            selected_index: updatedResponse.selected_index,
            needs_review: updatedResponse.needs_review,
            correct: updatedResponse.correct,
            score: updatedResponse.score,
            content_score: updatedResponse.content_score,
            grammar_score: updatedResponse.grammar_score,
            content_feedback: updatedResponse.content_feedback,
            grammar_feedback: updatedResponse.grammar_feedback,
            grammar_evaluated: updatedResponse.grammar_evaluated,
            transcription_verified: updatedResponse.transcription_verified,
            grammar_observations: updatedResponse.grammar_observations || [],
            display_value,
            status: hasAnswer ? 'answered' : 'unanswered',
          }
        }
      };
    }));
  };

  // Get expanded student/row data for the detail pane
  const expandedStudent = expanded ? students.find(s => s.id === expanded.studentId) : null;
  const expandedRowData = expanded ? gridData.find(r => r.student_id === expanded.studentId) : null;

  // Build breadcrumb
  const scrollTitle = lesson_context ? (
    <>
      <Link href={`/teacher/courses/${lesson_context.course_id}`} className="hover:text-blue-600 dark:hover:text-blue-400">
        {lesson_context.course_title}
      </Link>
      {'  ›  '}
      <Link href={lesson_context.from_lesson} className="hover:text-blue-600 dark:hover:text-blue-400">
        {lesson_context.title}
      </Link>
      {'  ›  '}
      <Link href={`/teacher/activities/${activity.id}/edit`} className="hover:text-blue-600 dark:hover:text-blue-400">
        {activity.title}
      </Link>
      {'  ›  Submissions'}
    </>
  ) : (
    <>
      <Link href={`/teacher/activities/${activity.id}/edit`} className="hover:text-blue-600 dark:hover:text-blue-400">
        {activity.title}
      </Link>
      {'  ›  Submissions'}
    </>
  );

  const backLink = `/teacher/activities/${activity.id}/edit`;

  return (
    <TeacherShell maxWidth="wide" scrollTitle={scrollTitle}>
      {unit_context && (
        <CompactUnitView unitContext={unit_context} />
      )}

      <DynamicIsland
        isActive={evaluating}
        steps={[
          `Evaluating ${unevaluatedCount} response${unevaluatedCount !== 1 ? 's' : ''}…`,
          'Analyzing content accuracy…',
          'Evaluating grammar…',
          'Scoring observations…',
        ]}
        stepDuration={2500}
      />

      <TeacherPageHeader
        title="Homework Submissions"
        subtitle={activity.title}
        backLink={backLink}
        backLabel="Back to activity"
        actions={unevaluatedCount > 0 && !evaluating && (
          <button
            onClick={handleEvaluateAll}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors whitespace-nowrap"
          >
            Evaluate All ({unevaluatedCount})
          </button>
        )}
      />

      <div className="space-y-4">
        {/* Filter bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setFilter('all')}
              className={cn(
                'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                filter === 'all'
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'
              )}
            >
              All Students ({students.length})
            </button>
            <button
              onClick={() => setFilter('needs_review')}
              className={cn(
                'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                filter === 'needs_review'
                  ? 'bg-amber-500 text-white'
                  : 'bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/20 dark:text-amber-400 dark:hover:bg-amber-900/30'
              )}
            >
              Needs Review ({needsReviewCount})
            </button>
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {filteredStudents.length} student{filteredStudents.length !== 1 ? 's' : ''}
            {' \u2022 '}
            {questions.length} question{questions.length !== 1 ? 's' : ''}
          </div>
        </div>

        <SessionMapLegend variant="homework" />

        {/* Grid — scrolls horizontally on its own */}
        <Card padding="none" className="overflow-hidden">
          <div className="overflow-x-auto">
            <SessionMapGrid
              questions={questions}
              students={filteredStudents}
              gridData={gridData}
              emptyStudentsMessage="No students have submitted homework for this activity."
              emptyQuestionsMessage="No approved questions in this activity."
              onCellClick={handleCellClick}
              selectedCell={expanded}
              printHref={(student) => `/teacher/activities/${activity.id}/scan_pdf?student_id=${student.id}`}
              onRevokeScanToken={handleRevokeScanToken}
              revokingStudentId={revokingStudentId}
            />
          </div>
        </Card>

        {/* Review pane — rendered outside the grid at full container width */}
        {expanded && expandedStudent && expandedRowData && (
          <Card padding="none" className="overflow-hidden">
            <SubmissionExpand
              key={expanded.studentId}
              student={expandedStudent}
              rowData={expandedRowData}
              questions={questions}
              activityId={activity.id}
              focusedQuestionId={expanded.questionId}
              grammarConcepts={grammar_concepts}
              onClose={() => setExpanded(null)}
              onSaved={(questionId, updatedResponse) => handleSaved(expandedStudent.id, questionId, updatedResponse)}
            />
          </Card>
        )}
      </div>
    </TeacherShell>
  );
}
